import { forwardRef, type CSSProperties } from "react";
import { Button, CircularProgress, type ButtonProps } from "@mui/material";
import type { CSSObject } from "@mui/material/styles";
import Text from "@CubeComponents/html/Text/Text";
import IconComponent from "@CubeComponents/icons";

const BUTTON_TYPES = {
  GRADIENT_BUTTON: "gradientButton",
  BACKGROUND_BUTTON: "backgroundButton",
  BORDER_BUTTON: "borderButton",
  TEXT_BUTTON: "textButton",
  INFO_GRADIENT_BUTTON: "infoGradientButton",
  INFO_BACKGROUND_BUTTON: "infoBackgroundButton",
  ACTIVE_BUTTON: "activeButton",
  IN_ACTIVE_BUTTON: "inActiveButton",
  IN_PROGRESS_BUTTON: "inProgressButton",
  TO_START: "toStart",
  VARIES: "varies",
  NO_STATUS: "noStatus",
  ANY_STATUS: "anyStatus",
};

type LoadingPosition = NonNullable<ButtonProps["loadingPosition"]>;

const SPINNER_SIZE = "1rem";
const SPINNER_GAP = "0.5rem";
const SPINNER_OFFSET = `calc(100% + ${SPINNER_GAP})`;
const LABEL_OFFSET = `calc((${SPINNER_SIZE} + ${SPINNER_GAP}) / 2)`;

const SPINNER_BASE: CSSProperties = { position: "absolute", insetBlock: 0, marginBlock: "auto" };

const SPINNER_PLACEMENT: Record<LoadingPosition, CSSProperties> = {
  start: { insetInlineEnd: SPINNER_OFFSET },
  end: { insetInlineStart: SPINNER_OFFSET },
  center: { insetInline: 0, marginInline: "auto", visibility: "visible" },
};

const LABEL_SHIFT: Record<LoadingPosition, CSSProperties> = {
  start: { insetInlineStart: LABEL_OFFSET },
  end: { insetInlineEnd: LABEL_OFFSET },
  center: {},
};

/**
 * Public surface of ButtonComponent. Everything not destructured below is spread onto MUI's
 * `Button`, so `ButtonProps` is the base — staging uses `LoadingButtonProps`, but MUI v9 folded
 * @mui/lab's LoadingButton into Button behind the `loading` prop. The second group are this
 * wrapper's own props; the third are props real call sites pass today which Button does not
 * declare — they are forwarded verbatim and end up as DOM attributes. They are listed so the
 * component stays exactly as permissive as it is today.
 */
type ButtonComponentProps = ButtonProps & {
  /** One of `BUTTON_TYPES`; selects the `sx` block and the MUI `variant` below. */
  buttonType?: string;
  iconButton?: boolean;
  iconButtonSecondary?: boolean;
  iconName?: string;
  boldFont?: boolean;
  maxWidth?: string | number;
  cssClass?: string;
  padding?: string | number;
  bgColor?: string;
  btnColor?: string;
  borderRadius?: string | number;
  height?: string | number;
  width?: string | number;
  textAlign?: string;
};

const ButtonComponent = forwardRef<HTMLButtonElement, ButtonComponentProps>(
  (
    {
      buttonType = BUTTON_TYPES.BACKGROUND_BUTTON,
      iconButton = false,
      className = "",
      loading = false,
      loadingPosition = "start",
      disabled = false,
      boldFont = true,
      sx,
      children,
      iconButtonSecondary,
      iconName,
      maxWidth,
      ...otherProps
    },
    ref,
  ) => {
    let variant = "";
    let style: CSSObject = {};
    let buttonPadding;
    const spinnerPosition: LoadingPosition = iconButton || iconButtonSecondary ? "center" : loadingPosition;

    if (iconButton) {
      buttonPadding = "0.25rem";
    } else if (iconButtonSecondary) {
      buttonPadding = "0.125rem";
    } else {
      buttonPadding = "0.125rem 1.25rem";
    }

    if ([BUTTON_TYPES.BACKGROUND_BUTTON, BUTTON_TYPES.INFO_BACKGROUND_BUTTON].includes(buttonType)) {
      variant = "contained";
      style = {
        ...style,
        background: `var(--button-light)`,
        borderRadius: buttonType == BUTTON_TYPES.BACKGROUND_BUTTON ? `var(--size-5)` : `var(--size-20)`,
        border: "none",
        color: `var(--primary-text)`,
        "&:hover": {
          background: "#D8DEEC",
        },
        "&:disabled": {
          background: `var(--button-light)`,
          color: `var(--disabled-text-gray)`,
          cursor: "not-allowed",
        },
        transition: "background 0.5s ease",
      };
    } else if ([BUTTON_TYPES.GRADIENT_BUTTON, BUTTON_TYPES.INFO_GRADIENT_BUTTON].includes(buttonType)) {
      variant = "contained";
      style = {
        ...style,
        background: `linear-gradient(100.44deg, var(--primary-gradient-color1) 0%, var(--primary-gradient-color2) 110.4%)`,
        borderRadius: buttonType == BUTTON_TYPES.GRADIENT_BUTTON ? `var(--size-5)` : `var(--size-20)`,
        color: `var(--surface-white) `,
        marginInlineStart: "0 !important",
        transition: "background 0.3s ease",
        "&:hover": {
          background: `linear-gradient(100.44deg, #8BC7E5 0%, #67A9D6 110.4%)`,
        },
        "&:disabled": {
          background: `#ACD3E7 !important`,
          color: `var(--surface-white) !important`,
          cursor: "not-allowed",
        },
      };
    } else if (buttonType == BUTTON_TYPES.BORDER_BUTTON) {
      style = {
        overflow: "unset",
        position: "relative",
        background: "var(--surface-white)",
        zIndex: 1,
        color: "var(--primary-text)",
        border: "1px solid #4592B7",
        borderRadius: "var(--size-4)",
        marginInlineStart: "0 !important",
        "&:hover": {
          background: "#E8E9ED",
        },
        "&:disabled": {
          background: "var(--surface-white)",
          color: "var(--disabled-text-gray)",
          border: "1px solid #B2D9ED",
          cursor: "var(--not-allowed) !important",
          borderRadius: "var(--size-5) !important",
        },
        transition: "background 0.3s ease",
      };
      variant = "outlined";
    } else if (buttonType == BUTTON_TYPES.TEXT_BUTTON) {
      variant = "text";
      style = {
        border: "none",
        borderRadius: "var(--size-5)",
        color: "var(--primary-text)",
        "&:hover": {
          background: "#E8E9ED",
        },
        "&:disabled": {
          background: `var(--none)`,
          WebkitBackgroundClip: "var(--padding-box)",
          WebkitTextFillColor: `var(--disabled-text-gray)`,
          borderRadius: "var(--size-5)",
          cursor: "var(--not-allowed)",
        },
        transition: "background 0.3s ease",
      };
    } else if (
      buttonType == "activeButton" ||
      buttonType == "inActiveButton" ||
      buttonType == "inProgressButton" ||
      buttonType == "varies" ||
      buttonType == "noStatus" ||
      buttonType == "anyStatus" ||
      buttonType == "toStart"
    ) {
      variant = "contained";
      let bgColor;
      let btnType = buttonType;

      if (btnType === BUTTON_TYPES.ACTIVE_BUTTON) {
        bgColor = "var(--positive-communication)";
      } else if (btnType === BUTTON_TYPES.IN_ACTIVE_BUTTON) {
        bgColor = "var(--negative-communication)";
      } else if (btnType === BUTTON_TYPES.IN_PROGRESS_BUTTON) {
        bgColor = "var(--warning)";
      } else if (btnType === BUTTON_TYPES.TO_START) {
        bgColor = "#666A84";
      } else if (btnType === BUTTON_TYPES.VARIES) {
        bgColor = "#6BBBE3";
      } else if (btnType === BUTTON_TYPES.NO_STATUS) {
        bgColor = "#B3B5C2";
      } else if (btnType === BUTTON_TYPES.ANY_STATUS) {
        bgColor = "#D4066D";
      }
      style = {
        background: bgColor,
        color: `var(--surface-white)`,
        border: "none",
        "&:disabled": {
          background: `var(--button-light)`,
          color: `var(--disabled-text-gray)`,
          cursor: "var(--not-allowed)",
        },
      };
    }

    return (
      <Button
        {...otherProps}
        disableElevation
        loadingPosition={loadingPosition}
        aria-busy={loading || undefined}
        className={className}
        ref={ref}
        // KNOWN BUG, preserved verbatim: `buttonType` is a free `string`, so any value outside the list
        // handled above leaves `variant` as "", which is not a MUI variant. It cannot be replaced without
        // changing rendering — MUI only falls back to its "text" default for `undefined`, so "" and
        // `undefined` paint differently. Fix: make `buttonType` a union of `BUTTON_TYPES` values and give
        // `variant` a real default.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- `variant` is "" for any unhandled `buttonType`; see above, the empty string is load-bearing for how those buttons render today
        variant={variant as ButtonProps["variant"]}
        sx={{
          // A default, not the `min-w-fit` utility: that one is layered !important and no caller's sx could narrow the button
          minWidth: "fit-content",
          whiteSpace: "nowrap",
          gap: loadingPosition !== "center" ? "0.5rem" : "",
          padding: buttonPadding,
          ...style,
          textTransform: "none",
          ...sx,
          "& .MuiCircularProgress-root": {
            color:
              buttonType === BUTTON_TYPES.GRADIENT_BUTTON || buttonType === BUTTON_TYPES.INFO_GRADIENT_BUTTON
                ? "white"
                : "inherit",
          },
        }}
        disabled={disabled || Boolean(loading)}
      >
        <span
          className={boldFont ? "font-semibold" : ""}
          style={{
            visibility: loading && spinnerPosition == "center" ? "hidden" : "inherit",
            maxWidth: maxWidth,
            ...(loading && { position: "relative", ...LABEL_SHIFT[spinnerPosition] }),
          }}
        >
          {iconButton ? (
            <IconComponent
              name={iconName}
              className={`w-6 h-6 ${disabled ? "opacity-40" : ""}`}
              width="1.5rem"
              height="1.5rem"
            />
          ) : iconButtonSecondary ? (
            <IconComponent
              name={iconName}
              className={`w-4 h-4 ${disabled ? "opacity-40" : ""}`}
              width="1.5rem"
              height="1.5rem"
            />
          ) : null}
          {children && <Text>{children}</Text>}{" "}
          {loading && (
            <CircularProgress
              aria-hidden
              color="inherit"
              size={SPINNER_SIZE}
              style={{ ...SPINNER_BASE, ...SPINNER_PLACEMENT[spinnerPosition] }}
            />
          )}
        </span>
      </Button>
    );
  },
);

ButtonComponent.displayName = "ButtonComponent";
export default ButtonComponent;
